import React, { useState, useEffect, useCallback } from 'react';
import { helpStyles } from '../../styles/helpStyles';
import { quickGuideData, QuickGuideSlide } from '../../data/quickGuideData';

interface QuickGuideProps {
  onComplete?: () => void;
}

export const QuickGuide: React.FC<QuickGuideProps> = ({ onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = quickGuideData;

  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
      if (index === slides.length - 1 && onComplete) {
        onComplete();
      }
    }
  }, [slides.length, onComplete]);

  const goNext = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const goPrev = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  // キーボード操作
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev]);

  const slide = slides[currentSlide];

  return (
    <div style={helpStyles.quickGuideContainer}>
      <div style={helpStyles.slideContainer}>
        <SlideCard slide={slide} />
      </div>
      <div style={helpStyles.navigation}>
        <button
          onClick={goPrev}
          disabled={currentSlide === 0}
          style={{
            ...helpStyles.navButton,
            ...(currentSlide === 0 ? helpStyles.navButtonDisabled : {}),
          }}
        >
          ◀
        </button>
        <div style={helpStyles.indicators}>
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                ...helpStyles.indicator,
                ...(index === currentSlide
                  ? helpStyles.indicatorActive
                  : helpStyles.indicatorInactive),
              }}
              aria-label={`スライド ${index + 1}`}
            />
          ))}
        </div>
        <button
          onClick={goNext}
          disabled={currentSlide === slides.length - 1}
          style={{
            ...helpStyles.navButton,
            ...(currentSlide === slides.length - 1 ? helpStyles.navButtonDisabled : {}),
          }}
        >
          ▶
        </button>
      </div>
      <div style={{ textAlign: 'center', color: '#a0a0c0', fontSize: 12 }}>
        {currentSlide + 1} / {slides.length}
      </div>
    </div>
  );
};

interface SlideCardProps {
  slide: QuickGuideSlide;
}

const SlideCard: React.FC<SlideCardProps> = ({ slide }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div style={helpStyles.slide}>
      <div style={helpStyles.slideIcon}>{slide.icon}</div>
      <h3 style={helpStyles.slideTitle}>{slide.title}</h3>

      {slide.image && !imageError && (
        <img
          src={slide.image}
          alt={slide.title}
          style={helpStyles.slideImage}
          onError={() => setImageError(true)}
        />
      )}

      <div style={helpStyles.slideContent}>
        {slide.points.map((point, index) => (
          <div key={index} style={helpStyles.slidePoint}>
            <span style={helpStyles.slideBullet}>•</span>
            <span>{point}</span>
          </div>
        ))}
      </div>

      {slide.highlight && (
        <div style={helpStyles.slideHighlight}>{slide.highlight}</div>
      )}
    </div>
  );
};

export default QuickGuide;
